import { useCallback, useEffect, useState } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";

export interface PageSubmission {
  id: string;
  pageId: string;
  versionId: string;
  componentId: string | null;
  data: Record<string, unknown>;
  bindings: Record<string, unknown>;
  contactId: string | null;
  contactEmail: string | null;
  referrer: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  consentGiven: boolean | null;
  consentText: string | null;
  affiliateCode: string | null;
  processedAt: string | null;
  createdAt: string;
}

export interface PageSubmissionAnalytics {
  total: number;
  today: number;
  conversionRate: number;
  abandonmentCount: number;
  abandonmentRate: number;
  topDropOffFields: Array<{
    formId: string;
    fieldId: string;
    views: number;
    starts: number;
    completions: number;
    abandonmentCount: number;
    dropOffRate: number;
  }>;
}

export function usePageSubmissions(pageId: string | null | undefined) {
  const [submissions, setSubmissions] = useState<PageSubmission[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiFetch<{ data: { items: PageSubmission[]; total: number } }>(
        pageId
          ? `/crm/pages/forms/${pageId}/submissions?limit=200`
          : "/crm/pages/forms/submissions?limit=200",
      );
      setSubmissions(response.data.items ?? []);
      setTotal(response.data.total ?? 0);
    } catch (loadError) {
      setSubmissions([]);
      setTotal(0);
      setError(loadError instanceof ApiError ? loadError.message : "Failed to load submissions.");
    } finally {
      setIsLoading(false);
    }
  }, [pageId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { submissions, total, isLoading, error, refresh };
}

export function usePageSubmissionAnalytics(pageId: string | null | undefined) {
  const [analytics, setAnalytics] = useState<PageSubmissionAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiFetch<{ data: PageSubmissionAnalytics }>(
        pageId
          ? `/crm/pages/forms/${pageId}/analytics`
          : "/crm/pages/forms/analytics",
      );
      setAnalytics(response.data);
    } catch (loadError) {
      setAnalytics(null);
      setError(loadError instanceof ApiError ? loadError.message : "Failed to load submission analytics.");
    } finally {
      setIsLoading(false);
    }
  }, [pageId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { analytics, isLoading, error, refresh };
}
